import React, { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Container, Palette, Typography, emphasize, useTheme } from '@mui/material';
import LineChart, { LineChartData } from '../Dashboard/Widgets/Reusable/LineChart';
import { selectUserTimeFormat } from '@/redux/selectors/authSelectors';
import { MatrixedChartValues, generateColorArrayFromConfigs, generateRandomNumbers } from '../Dashboard/Widgets/Reusable/chartUtils';
import { generateChartColorMap } from '../Dashboard/Widgets/Reusable/chartConfig';
import { ConfigContext } from '@/contextAPI/configContext';
import { IngestManagementConfig } from '@/__configs/config_types';
import { useAppSelector } from '@/redux/hooks';
import axios from '@/_utils/axios';
import { useToast } from '../_common/Toast';

interface IngestManagementChartProps {}

const keyLabels = {
  new_count: 'New Files',
  pending_count: 'Pending Files',
  ingested_count: 'Total Ingested Files'
};

const buildLineChartData = (data: any, chartColorOverrides: string[], is24HourTime: boolean): LineChartData => {
  const parsed = {
    id: 'ingest-management-chart',
    colors: chartColorOverrides?.length > 0 ? chartColorOverrides : ['primary', 'secondary', 'default'],
    labels: [] as string[],
    values: [] as MatrixedChartValues[],
    legend: 'tile',
    axes: {
      x: 'Time',
      y: 'No. of files ingested/reingested'
    }
  } as LineChartData;

  // build empty lines
  const first = data[0];
  Object.keys(first).forEach((key: string) => {
    if (Object.keys(keyLabels).includes(key)) {
      parsed.values.push({
        label: keyLabels[key as keyof typeof keyLabels],
        data: []
      });
    }
  });

  // iterate through dates and add data to lines
  data.forEach((item: any) => {
    const date = new Date(item.date);
    parsed.labels.push(
      is24HourTime
        ? date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: false,
            // timeZone: 'UTC'
          })
        : date
            .toLocaleTimeString('en-US', {
              hour: 'numeric',
              hour12: true,
              // timeZone: 'UTC'
            })
            .toLowerCase()
    );

    // add the data to the built lines
    Object.keys(item)
      .filter((key) => Object.keys(keyLabels).includes(key))
      .forEach((key: string, index) => {
        parsed.values[index].data.push(item[key]);
      });
  });

  // if the cumulative total of the total line data is 0, add the prop 'noDataMessage' to the parsed object
  if (
    parsed.values.reduce((acc: number, line: any) => {
      return acc + line.data.reduce((sum: number, num: number) => sum + num, 0);
    }, 0) === 0
  ) {
    parsed.noDataMessage = 'No data available';
  }

  return parsed;
};

const generateDefaultChart = (colors: any): LineChartData => {
  const time = [];
  for (let i = 5; i >= 0; i--) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    time.push(`${month} ${year}`);
  }

  const lines = Object.keys(keyLabels).map((label) => ({
    label: label,
    data: generateRandomNumbers(12, 25)
  }));

  return {
    id: 'ingest-management-chart',
    noDataMessage: 'Loading Data...',
    labels: time,
    values: lines,
    colors: colors,
    legend: 'tile',
    axes: {
      x: 'Time',
      y: 'No. of files ingested/reingested'
    }
  };
};

const IngestManagementChart = ({}: IngestManagementChartProps) => {
  const is24HourTime = useAppSelector(selectUserTimeFormat).startsWith('HH');
  const colorMap = generateChartColorMap(useTheme().palette);
  const { colors, glow } = useContext(ConfigContext).ingestManagementConfig.chart || {};
  const dashboardConfig = useContext(ConfigContext)?.dashboardConfig;
  const { error } = useToast();
  const colorConfig = generateColorArrayFromConfigs(6, dashboardConfig.chartColors, colors?.length ? colors : []);
  const [lineChartData, setLineChartData] = useState<LineChartData>(generateDefaultChart(colorConfig));

  useEffect(() => {
    axios.get('/ingest_manager/ingest_records/stats/').then((res: any) => {
      const statsData = res?.data?.data ? res.data.data.reverse() : [];
      if (statsData.length > 0) {
        setLineChartData(buildLineChartData(statsData, colorConfig, is24HourTime));
      } else {
        error('Error fetching Incoming Files data');
      }
    });
  }, []);

  const maxValues = lineChartData.values.map((line) => Math.max(...line.data));
  const suggestedMax = Math.max(...maxValues) * 1.2;

  return (
    <Wrapper>
      <ChartContainer glow={glow}>
        <HeaderDiv>
          <Typography variant="h5">Incoming files</Typography>
          <Notice>Overview of the incoming files within 24 Hours</Notice>
        </HeaderDiv>
        <LineChart
          loading={false}
          title="Ingested Files"
          data={lineChartData}
          colors={colorMap}
          animation={dashboardConfig?.chartAnimation}
          height={167}
          options={{
            scales: {
              x: {
                ticks: {
                  maxTicksLimit: 12
                }
              },
              y: {
                suggestedMax: suggestedMax < 10 ? 10 : suggestedMax
              }
            }
          }}
        />
      </ChartContainer>
    </Wrapper>
  );
};

export default IngestManagementChart;

const Wrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(2.5, 4)};
  background: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.white)};
`;

const HeaderDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(2, 4, 0)};
  min-height: fit-content;
`;

const Notice = styled.div`
  font-size: 1.3rem;
  font-weight: 400;
  line-height: 20.43px;
  opacity: 0.5;
  height: 22px;
`;

const ChartContainer = styled(Container)<{ glow: IngestManagementConfig['chart']['glow'] }>`
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
  border: 2px solid ${({ theme }) => theme.palette.background.default};
  box-shadow: 0px 12px 26px 0px #101e730f;
  background: ${({ theme }) =>
    `linear-gradient(
  5deg, 
  rgba(0, 0, 0, 0) 6%, 
  ${theme.palette.background.paper} 50%, 
  ${emphasize(theme.palette.background.paper, 0.1)} 89%)
  `};

  > div:nth-of-type(2) {
    margin: ${({ theme }) => theme.spacing(0.5, 0, 2.5, 0)};
  }

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    left: 0px;
    height: 100%;
    opacity: ${({ glow }) => glow?.opacity || 0.2};
    background: ${({ theme, glow }) => `
    linear-gradient(186deg, rgba(0, 0, 0, 0) 27%, ${
      glow?.color.startsWith('#') ? glow.color : theme.palette[(glow?.color as keyof Palette) || 'primary'].main
    } 45%, rgba(0, 0, 0, 0) 76%)`};
  }
`;
